<template>
  <v-card variant="flat" border class="part-card" :class="{ 'part-card--low': low }">
    <div class="d-flex align-stretch">
      <!-- picture -->
      <div class="part-card__media d-flex align-center justify-center" @click="item.image && $emit('zoom', item)">
        <img v-if="item.image" :src="item.image_url" alt="" loading="lazy" decoding="async" />
        <v-icon v-else color="grey-lighten-1">mdi-package-variant-closed</v-icon>
      </div>

      <!-- identity -->
      <div class="flex-grow-1 pa-2 overflow-hidden">
        <div v-if="showCategory" class="part-card__category text-truncate">
          <v-icon size="x-small" class="mr-1">mdi-shape-outline</v-icon>{{ item.category_name || $t("part_view.no_category") }}
        </div>
        <div class="part-card__name font-weight-bold">{{ name }}</div>
        <div v-if="item.ref" class="text-caption text-medium-emphasis text-truncate">{{ item.ref }}</div>
        <div class="d-flex flex-wrap align-center mt-1 part-card__tags">
          <v-chip
            v-for="place in item.place_names || []"
            :key="place"
            size="x-small"
            label
            variant="tonal"
            prepend-icon="mdi-map-marker-outline"
          >{{ place }}</v-chip>
          <v-chip
            v-if="isUnit && qrCount > 0"
            size="x-small"
            label
            variant="tonal"
            color="primary"
            prepend-icon="mdi-qrcode"
            @click="$emit('serials', item)"
          >{{ qrCount }}</v-chip>
        </div>
      </div>

      <!-- quantity -->
      <div class="part-card__qty d-flex flex-column align-center justify-center px-3" :class="low ? 'text-error' : 'text-primary'">
        <div class="text-h5 font-weight-bold lh-1">{{ quantity }}</div>
        <div class="text-caption">{{ $filters.unityFilter(item.unity) }}</div>
        <div v-if="!isNone(item.min_quantity)" class="text-caption text-medium-emphasis">
          {{ $t("part_view.min_short") }} {{ item.min_quantity }}
        </div>
      </div>
    </div>

    <v-divider></v-divider>

    <!-- actions: the two frequent ones as buttons, the rest in a menu -->
    <div class="d-flex align-center pa-1 part-card__actions">
      <v-btn
        variant="tonal"
        color="success"
        size="small"
        prepend-icon="mdi-plus"
        class="flex-grow-1"
        @click="$emit('add', item)"
      >{{ $filters.capitalize($t("add")) }}</v-btn>
      <v-btn
        v-if="staff && item.quantity > 0"
        variant="tonal"
        color="warning"
        size="small"
        prepend-icon="mdi-minus"
        class="flex-grow-1"
        @click="$emit('remove', item)"
      >{{ $filters.capitalize($t("actions.delete_item")) }}</v-btn>
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" icon="mdi-dots-vertical" size="small" variant="text"></v-btn>
        </template>
        <v-list density="compact">
          <v-list-item
            v-if="isUnit && item.quantity > 0"
            prepend-icon="mdi-barcode"
            :title="$filters.capitalize($t('actions.show_serials'))"
            @click="$emit('serials', item)"
          ></v-list-item>
          <v-list-item
            prepend-icon="mdi-pencil-outline"
            :title="$filters.capitalize($t('actions.edit'))"
            @click="$emit('edit', item)"
          ></v-list-item>
          <v-list-item
            v-if="staff"
            prepend-icon="mdi-delete-outline"
            base-color="error"
            :title="$filters.capitalize($t('actions.delete'))"
            @click="$emit('delete', item)"
          ></v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-card>
</template>

<script>
import { isNone } from "@/functions";

export default {
  name: "PartMobileCard",
  props: {
    item: { type: Object, required: true },
    staff: { type: Boolean, default: false },
    low: { type: Boolean, default: false },
    // false when the card already sits under its category panel
    showCategory: { type: Boolean, default: true },
  },
  emits: ["zoom", "add", "remove", "serials", "edit", "delete"],
  computed: {
    name() {
      const en = this.$store.getters.language === "en";
      return en && this.item.name_en ? this.item.name_en : this.item.name;
    },
    isUnit() {
      return this.item.unity === "un";
    },
    qrCount() {
      return Array.isArray(this.item.qr_codes) ? this.item.qr_codes.length : 0;
    },
    quantity() {
      return isNone(this.item.quantity) ? 0 : this.item.quantity;
    },
  },
  methods: { isNone },
};
</script>

<style scoped>
.part-card {
  border-radius: 10px;
  overflow: hidden;
}
.part-card--low {
  border-left: 4px solid rgb(var(--v-theme-error)) !important;
}
.part-card__media {
  width: 72px;
  flex: 0 0 72px;
  background: #f5f5f5;
}
.part-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.part-card__category {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  line-height: 1.3;
}
.part-card__name {
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.part-card__tags {
  gap: 4px;
}
.part-card__qty {
  min-width: 4.5em;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}
.part-card__actions {
  gap: 6px;
}
.lh-1 {
  line-height: 1.1;
}
</style>
